import { useState, useMemo } from 'react';
import { AlertTriangle, Clock, UserCheck, Calendar, User, Users, Columns3 } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { DeliverableStatusBadge } from './DeliverableStatusBadge';
import { useUpdateDeliverable } from '@/hooks/useDeliverables';
import type { Deliverable, DeliverableStatus } from '@/hooks/useDeliverables';
import { useNavigate, type NavigateFunction } from 'react-router-dom';

const BOARD_COLUMNS: { status: DeliverableStatus; label: string; color: string; bgColor: string }[] = [
  { status: 'pending', label: 'Pending', color: 'border-gray-300', bgColor: 'bg-gray-50' },
  { status: 'in_progress', label: 'In Progress', color: 'border-blue-400', bgColor: 'bg-blue-50/50' },
  { status: 'in_review', label: 'In Review', color: 'border-yellow-400', bgColor: 'bg-yellow-50/50' },
  { status: 'revision', label: 'Revision', color: 'border-orange-400', bgColor: 'bg-orange-50/50' },
  { status: 'approved', label: 'Approved', color: 'border-green-400', bgColor: 'bg-green-50/50' },
  { status: 'delivered', label: 'Delivered', color: 'border-emerald-500', bgColor: 'bg-emerald-50/50' },
];

function timeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffDays > 0) return `${diffDays}d`;
  if (diffHours > 0) return `${diffHours}h`;
  return 'just now';
}

function isStale(deliverable: Deliverable, staleDays: number = 3): boolean {
  if (!['in_review', 'revision'].includes(deliverable.status)) return false;
  if (!deliverable.status_changed_at) return false;
  const now = new Date();
  const changed = new Date(deliverable.status_changed_at);
  const diffDays = (now.getTime() - changed.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= staleDays;
}

function isOverdue(deliverable: Deliverable): boolean {
  if (!deliverable.due_date) return false;
  if (deliverable.status === 'delivered' || deliverable.status === 'approved') return false;
  return new Date(deliverable.due_date + 'T00:00:00') < new Date();
}

function getBallHolder(deliverable: Deliverable): { name: string; role: string } | null {
  switch (deliverable.status) {
    case 'in_progress':
      return deliverable.assigned_to ? { name: deliverable.assigned_to, role: 'working' } : null;
    case 'in_review':
      return deliverable.reviewer
        ? { name: deliverable.reviewer, role: 'reviewing' }
        : deliverable.assigned_to ? { name: deliverable.assigned_to, role: 'reviewing' } : null;
    case 'revision':
      return deliverable.assigned_to ? { name: deliverable.assigned_to, role: 'revising' } : null;
    case 'approved':
      return { name: 'PM', role: 'to deliver' };
    default:
      return null;
  }
}

type BoardGrouping = 'status' | 'assignee';

interface DeliverableBoardProps {
  deliverables: (Deliverable & { project_name?: string })[];
}

export function DeliverableBoard({ deliverables }: DeliverableBoardProps) {
  const navigate = useNavigate();
  const updateDeliverable = useUpdateDeliverable();
  const [grouping, setGrouping] = useState<BoardGrouping>('status');
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Bucket deliverables by status
  const statusColumns = useMemo(() => {
    const buckets = new Map<DeliverableStatus, (Deliverable & { project_name?: string })[]>();
    for (const col of BOARD_COLUMNS) {
      buckets.set(col.status, []);
    }
    for (const d of deliverables) {
      const bucket = buckets.get(d.status);
      if (bucket) bucket.push(d);
    }
    return buckets;
  }, [deliverables]);

  // Bucket deliverables by assignee (ball holder)
  const assigneeColumns = useMemo(() => {
    const buckets = new Map<string, (Deliverable & { project_name?: string })[]>();
    for (const d of deliverables) {
      const holder = getBallHolder(d);
      const key = holder?.name || d.assigned_to || 'Unassigned';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(d);
    }
    return buckets;
  }, [deliverables]);

  const activeDraggable = activeId ? deliverables.find(d => d.id === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const deliverableId = active.id as string;
    const newStatus = over.id as DeliverableStatus;

    // Only update if status actually changed
    const deliverable = deliverables.find(d => d.id === deliverableId);
    if (deliverable && deliverable.status !== newStatus) {
      updateDeliverable.mutateAsync({ id: deliverableId, data: { status: newStatus } });
    }
  };

  if (deliverables.length === 0) return null;

  // Assign colors to assignees
  const ASSIGNEE_COLORS = [
    { color: 'border-blue-400', bgColor: 'bg-blue-50/50' },
    { color: 'border-violet-400', bgColor: 'bg-violet-50/50' },
    { color: 'border-teal-400', bgColor: 'bg-teal-50/50' },
    { color: 'border-pink-400', bgColor: 'bg-pink-50/50' },
    { color: 'border-amber-400', bgColor: 'bg-amber-50/50' },
    { color: 'border-indigo-400', bgColor: 'bg-indigo-50/50' },
    { color: 'border-rose-400', bgColor: 'bg-rose-50/50' },
    { color: 'border-cyan-400', bgColor: 'bg-cyan-50/50' },
  ];

  return (
    <div className="space-y-3 min-w-0">
      {/* Grouping toggle */}
      <div className="flex items-center gap-0.5 border rounded-md w-fit">
        <button
          className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors ${
            grouping === 'status'
              ? 'bg-primary text-primary-foreground rounded-[4px]'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setGrouping('status')}
        >
          <Columns3 className="h-3 w-3" />
          By Status
        </button>
        <button
          className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors ${
            grouping === 'assignee'
              ? 'bg-primary text-primary-foreground rounded-[4px]'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setGrouping('assignee')}
        >
          <Users className="h-3 w-3" />
          By Person
        </button>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 pb-2" style={{ minHeight: 200 }}>
        {grouping === 'assignee' ? (
          // Assignee columns (no drag-drop — status grouping only)
          Array.from(assigneeColumns.entries()).map(([name, items], idx) => {
            const colorSet = ASSIGNEE_COLORS[idx % ASSIGNEE_COLORS.length];
            return (
              <div
                key={name}
                className={`flex-1 min-w-[180px] rounded-lg border-t-2 ${colorSet.color} ${colorSet.bgColor} flex flex-col`}
              >
                <div className={`flex items-center justify-between px-3 py-2 border-b sticky top-0 z-10 ${colorSet.bgColor} backdrop-blur-sm`}>
                  <span className="text-xs font-semibold flex items-center gap-1.5">
                    <div className={`h-2 w-2 rounded-full ${colorSet.color.replace('border-', 'bg-')}`} />
                    {name}
                  </span>
                  <span className="text-[10px] text-muted-foreground bg-background rounded-full px-1.5 py-0.5 border">
                    {items.length}
                  </span>
                </div>
                <div className="flex-1 p-2 space-y-2">
                  {items.map((d) => (
                    <BoardCard key={d.id} deliverable={d} navigate={navigate} showAssignee={false} />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          // Status columns with drag-and-drop
          BOARD_COLUMNS.map((col) => {
          const items = statusColumns.get(col.status) || [];
          // Skip delivered column if empty
          if (col.status === 'delivered' && items.length === 0) return null;

          return (
            <DroppableColumn key={col.status} status={col.status} color={col.color} bgColor={col.bgColor} label={col.label} count={items.length}>
              {items.length === 0 && (
                <div className="text-center py-6">
                  <p className="text-[10px] text-muted-foreground/60">No items</p>
                </div>
              )}
              {items.map((d) => (
                <DraggableBoardCard key={d.id} deliverable={d} navigate={navigate} />
              ))}
            </DroppableColumn>
          );
        })
        )}
        </div>

        <DragOverlay>
          {activeDraggable && (
            <div className="opacity-90">
              <BoardCard deliverable={activeDraggable} navigate={navigate} showAssignee={true} />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

// --- Droppable column wrapper ---

function DroppableColumn({
  status,
  color,
  bgColor,
  label,
  count,
  children,
}: {
  status: DeliverableStatus;
  color: string;
  bgColor: string;
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 min-w-[180px] rounded-lg border-t-2 ${color} ${bgColor} flex flex-col transition-all ${
        isOver ? 'ring-2 ring-primary/50 bg-primary/5' : ''
      }`}
    >
      <div className={`flex items-center justify-between px-3 py-2 border-b sticky top-0 z-10 ${bgColor} backdrop-blur-sm`}>
        <span className="text-xs font-semibold">{label}</span>
        <span className="text-[10px] text-muted-foreground bg-background rounded-full px-1.5 py-0.5 border">
          {count}
        </span>
      </div>
      <div className="flex-1 p-2 space-y-2">
        {children}
      </div>
    </div>
  );
}

// --- Draggable card wrapper ---

function DraggableBoardCard({
  deliverable,
  navigate,
}: {
  deliverable: Deliverable & { project_name?: string };
  navigate: NavigateFunction;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: deliverable.id,
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={isDragging ? 'opacity-30' : ''}
    >
      <BoardCard deliverable={deliverable} navigate={navigate} showAssignee={true} />
    </div>
  );
}

// --- Extracted card component ---

function BoardCard({
  deliverable: d,
  navigate,
  showAssignee = true,
}: {
  deliverable: Deliverable & { project_name?: string };
  navigate: (path: string) => void;
  showAssignee?: boolean;
}) {
  const stale = isStale(d);
  const overdue = isOverdue(d);
  const ballHolder = getBallHolder(d);

  return (
    <div
      className={`rounded-md border bg-card p-2.5 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 ${
        stale
          ? 'border-red-300 ring-1 ring-red-200'
          : overdue
            ? 'border-amber-300'
            : 'border-border'
      }`}
      onClick={() => navigate(`/projects/${d.project_id}?section=deliverables`)}
    >
      {/* Status badge (shown in assignee grouping) */}
      {!showAssignee && (
        <div className="mb-1.5">
          <DeliverableStatusBadge status={d.status} />
        </div>
      )}

      {/* Title */}
      <p className="text-sm font-medium leading-tight mb-1.5">{d.title}</p>

      {/* Project name */}
      {d.project_name && (
        <p className="text-[10px] text-muted-foreground truncate mb-2">{d.project_name}</p>
      )}

      {/* Scope badge */}
      {d.scope_name && (
        <span className="inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-200 mb-2">
          {d.scope_name}
        </span>
      )}

      {/* Meta row */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Ball holder */}
        {showAssignee && ballHolder && (
          <span className="flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full border border-blue-200">
            <UserCheck className="h-2.5 w-2.5" />
            {ballHolder.name}
          </span>
        )}

        {/* Assignee when no ball holder */}
        {showAssignee && !ballHolder && d.assigned_to && (
          <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
            <User className="h-2.5 w-2.5" />
            {d.assigned_to}
          </span>
        )}

        {/* Time in status */}
        {d.status_changed_at && d.status !== 'pending' && d.status !== 'delivered' && (
          <span
            className={`flex items-center gap-0.5 text-[10px] ${
              stale ? 'text-red-600 font-semibold' : 'text-muted-foreground'
            }`}
          >
            {stale && <AlertTriangle className="h-2.5 w-2.5" />}
            <Clock className="h-2.5 w-2.5" />
            {timeAgo(d.status_changed_at)}
          </span>
        )}

        {/* Due date */}
        {d.due_date && (
          <span
            className={`flex items-center gap-0.5 text-[10px] ml-auto ${
              overdue ? 'text-red-600 font-semibold' : 'text-muted-foreground'
            }`}
          >
            <Calendar className="h-2.5 w-2.5" />
            {new Date(d.due_date + 'T00:00:00').toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </span>
        )}
      </div>

      {/* Stale warning */}
      {stale && (
        <div className="mt-1.5 text-[9px] text-red-600 font-medium flex items-center gap-1 bg-red-50 rounded px-1.5 py-0.5">
          <AlertTriangle className="h-2.5 w-2.5" />
          Stale — waiting {timeAgo(d.status_changed_at)}
        </div>
      )}
    </div>
  );
}
